import React, { useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { Button, Form, Input, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import get from 'lodash/get';

import Icon from 'components/Icon';
import TextArea from 'components/Input';
import Popup from 'components/Popup';
import Tag from 'components/Tag';
import ShareModalContainer from 'modules/Media/components/ShareModal/Container';

import { usePostShareMutation } from 'apiTyped/hooks/post.mutations';
import { components } from 'apiTyped/types';

const isMobile = window.innerWidth < 845;

type FormShareModal = {
  emails: number[];
  comment: string;
};

const SocialLink = ({ url, icon }: { url: string; icon: string }) => (
  <div className="shareIconContainer">
    <a href={url} target="_blank" rel="noopener noreferrer">
      <Icon icon={icon} alt={`social-network-${icon}`} />
    </a>
  </div>
);

const ShareModal = ({
  article,
  open,
  onClose
}: {
  article: components['schemas']['Post'];
  open: boolean;
  onClose: () => void;
}) => {
  const postShareMutation = usePostShareMutation(article.id);

  const [form] = Form.useForm();
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [invalid, setInvalid] = useState<boolean>(false);
  const url = get(article, 'content.url', document.location.href);

  const onSubmit = async (values: FormShareModal) => {
    if (!emails.length) {
      setInvalid(true);
      return;
    }

    postShareMutation.mutate({
      mails: emails,
      comment: get(values, 'comment', '')
    });
    onCancel();
  };

  const onCancel = () => {
    form.resetFields();
    setEmails([]);
    setInputValue('');
    onClose();
  };

  const handleEmailConfirm = () => {
    if (!validateEmail(inputValue)) {
      setInvalid(true);
      return;
    }

    setInvalid(false);
    if (inputValue && emails.indexOf(inputValue) === -1) {
      setEmails([...emails, inputValue]);
    }

    setInputValue('');
  };

  const handleEmailClose = (removedEmail: string) => {
    const newEmails = emails.filter((email) => email !== removedEmail);
    setEmails(newEmails);
  };

  const validateEmail = (email: string) => {
    const regexp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regexp.test(email);
  };

  return (
    <Popup
      open={open}
      title={I18n.t('media.shareModal.title')}
      icon="share"
      onClose={onCancel}
      hideButton
    >
      <ShareModalContainer>
        <Space direction={'vertical'}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onSubmit}
            onKeyUp={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            style={{ display: 'grid', gap: 6 }}
          >
            <div className="form-item-grid">
              <Form.Item
                name="mails"
                className="input-email"
                label={I18n.t('media.shareModal.shareTo').toUpperCase()}
                validateStatus={invalid ? 'error' : undefined}
                help={
                  invalid
                    ? I18n.t('media.shareModal.shareToMessageError')
                    : undefined
                }
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    placeholder={I18n.t('media.shareModal.shareToPlaceholder')}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onPressEnter={handleEmailConfirm}
                  />
                  <Button type="primary" onClick={handleEmailConfirm}>
                    <PlusOutlined />
                  </Button>
                </Space.Compact>
                <div id="scrollableDiv" className="scrollableDivEmails">
                  <Space direction="vertical">
                    {emails.map((email: string) => (
                      // @ts-expect-error Tag is js file, no validation
                      <Tag
                        icon="close"
                        iconPosition="right"
                        onClickIcon={() => {
                          handleEmailClose(email);
                        }}
                        key={`email-${email}`}
                        id={`email-${email}`}
                        label={email}
                        style={{
                          minHeight: 25,
                          fontSize: 12,
                          backgroundColor: 'rgba(134,142,150, 0.1)',
                          color: '#868e96',
                          border: 'none',
                          display: 'inline-flex'
                        }}
                      />
                    ))}
                  </Space>
                </div>
              </Form.Item>
              <Form.Item
                name="comment"
                label={I18n.t('media.shareModal.message').toUpperCase()}
              >
                <TextArea
                  type="textarea"
                  height="100px"
                  placeholder={I18n.t('media.shareModal.messagePlaceholder')}
                />
              </Form.Item>
            </div>
            {url && (
              <div className="shareContainer">
                {!isMobile && (
                  <p className="shareLabel">
                    {I18n.t('media.shareModal.shareNetworks')}{' '}
                  </p>
                )}
                <div className="shareIconsContainer">
                  <SocialLink
                    url={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
                    icon={'facebook'}
                  />
                  <SocialLink
                    url={`https://twitter.com/share?url=${url}`}
                    icon={'twitter'}
                  />
                  <SocialLink
                    url={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`}
                    icon={'linkedin'}
                  />
                </div>
              </div>
            )}
            <Form.Item>
              <div className="footer-modal">
                {!isMobile && (
                  <Button
                    size="large"
                    onClick={onCancel}
                    className="share-modal-button-cancel"
                  >
                    {I18n.t('cancel').toUpperCase()}
                  </Button>
                )}
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  className="share-modal-button-submit"
                >
                  {I18n.t('validate').toUpperCase()}
                </Button>
              </div>
            </Form.Item>
          </Form>
        </Space>
      </ShareModalContainer>
    </Popup>
  );
};

export default ShareModal;
